import {
  Alert,
  Box,
  Button,
  Dialog,
  FormControlLabel,
  Skeleton,
  Switch,
  Typography,
} from '@mui/material'
import axios from 'api/axios'
import { enqueueSnackbar } from 'notistack'
import { useCallback, useEffect, useRef, useState } from 'react'
import DialogoErrorReoptimizacion from './DialogoErrorReoptimizacion'
import DialogoReoptimizar from './DialogoReoptimizar'
import FiltrosGantt from './FiltrosGantt'
import './GanttPlanificacion.css'
import { configurarBloqueoEdicion, configurarLightbox } from './lightboxConfig'
import {
  configurarLabels,
  configurarNavegacion,
  configurarTemplates,
  configurarVistasTimeline,
} from './schedulerConfig'

export default function GanttPlanificacion({ idPlanificacion, onKpisLoaded }) {
  const containerRef = useRef(null)
  const schedulerRef = useRef(null)

  const notificacionMostradaRef = useRef(null)
  const [loading, setLoading] = useState(true)
  const [sinDistracciones, setSinDistracciones] = useState(false)
  const [datosGantt, setDatosGantt] = useState(null)
  const [cambiosPendientes, setCambiosPendientes] = useState([])
  const [filtrosActivos, setFiltrosActivos] = useState({
    fecha_desde: '',
    fecha_hasta: '',
    tipos_maquina: [],
    secciones: [],
  })
  const [mostrarTodasMaquinas, setMostrarTodasMaquinas] = useState(false)

  const [errorReoptimizacion, setErrorReoptimizacion] = useState(null)
  const [migrandoOracle, setMigrandoOracle] = useState(false)

  const toggleSinDistracciones = () => {
    // Limpiar cambios pendientes al cambiar de modo
    setCambiosPendientes([])
    setSinDistracciones((prev) => !prev)
  }

  // Fetch datos del Gantt con filtros
  const fetchDatosGantt = useCallback(
    async (filtros) => {
      if (!idPlanificacion) {
        return
      }

      try {
        setLoading(true)

        // Construir query params
        const params = new URLSearchParams()

        if (filtros.fecha_desde) params.append('fecha_desde', filtros.fecha_desde)
        if (filtros.fecha_hasta) params.append('fecha_hasta', filtros.fecha_hasta)

        filtros.tipos_maquina.forEach((tipo) => {
          params.append('tipo_maquina', tipo)
        })

        filtros.secciones.forEach((seccion) => {
          params.append('seccion', seccion)
        })

        const queryString = params.toString()
        const url = queryString
          ? `/planificaciones/${idPlanificacion}/gantt-filtrado/?${queryString}`
          : `/planificaciones/${idPlanificacion}/gantt/`

        const response = await axios.get(url)

        // Si no hay datos, mostrar mensaje y limpiar
        if (!response.data.data || response.data.data.length === 0) {
          const filtrosActivos = response.data.filtros_aplicados
          const tienesFiltros =
            filtrosActivos?.tipos_maquina?.length > 0 ||
            filtrosActivos?.secciones?.length > 0 ||
            filtrosActivos?.fecha_desde ||
            filtrosActivos?.fecha_hasta

          const mensaje = tienesFiltros
            ? 'No hay actividades con los filtros aplicados. Intente con otros criterios.'
            : 'No hay datos disponibles para esta planificación'

          if (
            !notificacionMostradaRef.current ||
            Date.now() - notificacionMostradaRef.current > 500
          ) {
            enqueueSnackbar(mensaje, { variant: 'info' })
            notificacionMostradaRef.current = Date.now()
          }
          setDatosGantt(null)
        } else {
          setDatosGantt(response.data)

          setCambiosPendientes([])

          // Extraer y pasar los KPIs al componente padre si existe el callback
          if (onKpisLoaded && (response.data.kpis || response.data.produccion_diaria)) {
            onKpisLoaded(
              response.data.kpis,
              response.data.produccion_diaria,
              response.data.produccion_diaria_por_articulo,
            )
          }

          if (
            !notificacionMostradaRef.current ||
            Date.now() - notificacionMostradaRef.current > 500
          ) {
            enqueueSnackbar(
              `${response.data.total_resultados || response.data.data?.length || 0} actividades cargadas`,
              { variant: 'success' },
            )
            notificacionMostradaRef.current = Date.now()
          }
        }
      } catch (error) {
        setDatosGantt(null)
        if (
          !notificacionMostradaRef.current ||
          Date.now() - notificacionMostradaRef.current > 500
        ) {
          enqueueSnackbar(
            'Error al cargar los datos del diagrama de Gantt: ' +
              (error.response?.data?.message || error.message),
            { variant: 'error' },
          )
          notificacionMostradaRef.current = Date.now()
        }
      } finally {
        setLoading(false)
      }
    },
    [idPlanificacion],
  )

  // Validar movimiento de actividad
  const validarMovimiento = useCallback(async (id, nuevoStart, nuevoEnd, nuevaMaquina) => {
    try {
      const response = await axios.post('/optprod/validar_movimiento/', {
        id_resultado: parseInt(id),
        id_maquina: parseInt(nuevaMaquina),
        start_manual: nuevoStart,
        fin_manual: nuevoEnd,
      })

      return response.data
    } catch (error) {
      return {
        valido: false,
        mensaje: 'Error al validar el movimiento',
        conflictos: [],
      }
    }
  }, [])

  const guardarCambiosPendientes = useCallback(async () => {
    if (cambiosPendientes.length === 0) {
      enqueueSnackbar('No hay cambios pendientes para guardar', { variant: 'info' })
      return
    }

    try {
      const response = await axios.post('/optprod/guardar_movimientos/', {
        movimientos: cambiosPendientes,
      })

      if (response.data.guardadas > 0) {
        // Limpiar cambios pendientes
        setCambiosPendientes([])

        // Notificación de éxito
        enqueueSnackbar(`${response.data.guardadas} cambio(s) guardado(s) correctamente`, {
          variant: 'success',
        })

        // Limpiar marcas visuales de pendientes
        if (schedulerRef.current) {
          cambiosPendientes.forEach((cambio) => {
            const evento = schedulerRef.current.getEvent(cambio.id_resultado)
            if (evento) {
              evento.pendiente = false
            }
          })
          schedulerRef.current.updateView()
        }

        // Recargar datos del Gantt desde el backend
        await fetchDatosGantt(filtrosActivos)
      }

      // Mostrar errores si los hay
      if (response.data.errores && response.data.errores.length > 0) {
        response.data.errores.forEach((error) => {
          enqueueSnackbar(`Error en movimiento ${error.movimiento}: ${error.error}`, {
            variant: 'warning',
          })
        })
      }
    } catch (error) {
      enqueueSnackbar(
        'Error al guardar cambios: ' + (error.response?.data?.message || error.message),
        { variant: 'error' },
      )
    }
  }, [cambiosPendientes, fetchDatosGantt, filtrosActivos])

  // Lanzar la migración de los resultados de la planificación hacia Oracle
  // a través del servicio LISA del ERP.
  const migrarAOracle = useCallback(async () => {
    if (!idPlanificacion) {
      return
    }
    setMigrandoOracle(true)
    try {
      await axios.post(`/optprod/migracion_oracle/${idPlanificacion}/`)
      enqueueSnackbar(
        `Migración a Oracle lanzada correctamente para la planificación ${idPlanificacion}`,
        { variant: 'success' },
      )
    } catch (error) {
      enqueueSnackbar(
        'Error al lanzar la migración a Oracle: ' +
          (error.response?.data?.detail || error.message),
        { variant: 'error' },
      )
    } finally {
      setMigrandoOracle(false)
    }
  }, [idPlanificacion])

  const confirmarReoptimizacion = useCallback(async () => {
    try {
      // Si hay cambios pendientes, guardarlos primero
      if (cambiosPendientes.length > 0) {
        const responseGuardar = await axios.post('/optprod/guardar_movimientos/', {
          movimientos: cambiosPendientes,
        })

        if (responseGuardar.data.guardadas === 0) {
          enqueueSnackbar('Error al guardar los cambios antes de reoptimizar', {
            variant: 'error',
          })
          return
        }
      }

      // Lanzar reoptimización
      const response = await axios.post(`/planificaciones/reoptimizar/${idPlanificacion}/`)
      enqueueSnackbar(response.data.detail || 'Planificación enviada a reoptimizar', {
        variant: 'success',
      })
    } catch (error) {
      if (error.response?.status === 409) {
        enqueueSnackbar(
          error.response?.data?.detail ||
            'Ya hay una optimización en ejecución. Espere a que termine.',
          { variant: 'warning' },
        )
      } else {
        setErrorReoptimizacion(
          error.response?.data?.detail || error.message || 'Error al reoptimizar la planificación',
        )
      }
    }
  }, [idPlanificacion, cambiosPendientes])

  // Cargar datos iniciales
  useEffect(() => {
    fetchDatosGantt(filtrosActivos)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchDatosGantt, JSON.stringify(filtrosActivos)])

  // Aplicar filtros
  const handleApplyFilters = useCallback((nuevosFiltros) => {
    setFiltrosActivos(nuevosFiltros)
  }, [])

  // Inicializar scheduler cuando lleguen los datos
  useEffect(() => {
    if (!datosGantt) return

    const datos = datosGantt.data

    if (!datos || datos.length === 0) return

    const inicializarScheduler = () => {
      if (!window.scheduler || !containerRef.current) {
        setTimeout(inicializarScheduler, 100)
        return
      }

      const rect = containerRef.current.getBoundingClientRect()

      if (!rect.width || !rect.height) {
        setTimeout(inicializarScheduler, 100)
        return
      }

      const scheduler = window.scheduler

      try {
        // Limpiar scheduler previo si existe
        if (schedulerRef.current) {
          scheduler.clearAll()
        }

        // Configuración base
        const formatoFecha = '%d-%m-%Y %H:%i'

        scheduler.config.xml_date = formatoFecha
        scheduler.config.readonly = false
        scheduler.config.drag_move = true
        scheduler.config.drag_resize = false
        scheduler.config.details_on_create = false
        scheduler.config.details_on_dblclick = true
        scheduler.config.drag_create = false
        scheduler.config.smart_rendering = true

        // Obtener máquinas según la opción seleccionada
        const sections = (() => {
          // Extraer máquinas de los datos (las que tienen actividades)
          const mapaMaquinasDescripciones = {}
          datosGantt.data.forEach((task) => {
            if (task.maquina && task.maquina_descripcion) {
              mapaMaquinasDescripciones[task.maquina] = task.maquina_descripcion
            }
          })

          const maquinasConActividades = [
            ...new Set(datosGantt.data.map((task) => task.maquina).filter(Boolean)),
          ]

          // Si mostrarTodosMaquinas y hay máquinas del backend, usarlas
          if (mostrarTodasMaquinas && datosGantt.maquinas_disponibles) {
            return datosGantt.maquinas_disponibles.sort((a, b) => Number(a.key) - Number(b.key))
          }

          // Si no, solo mostrar las que tienen actividades
          return maquinasConActividades
            .map((m) => ({
              key: m,
              label: mapaMaquinasDescripciones[m] || `Máquina ${m}`,
            }))
            .sort((a, b) => Number(a.key) - Number(b.key))
        })()

        // FIN: Extracción de máquinas

        // Vistas Timeline
        configurarVistasTimeline(scheduler, sections)

        // Navegación
        configurarNavegacion(scheduler)

        // Labels
        configurarLabels(scheduler)

        // Templates
        configurarTemplates(scheduler)

        // Configuración Lightbox
        configurarLightbox(scheduler, sections)

        configurarBloqueoEdicion(scheduler)

        // Evento de cambios
        scheduler.attachEvent('onEventChanged', function (id, ev) {
          return true
        })

        // Inicializar (usar la fecha de la primera tarea o fecha actual)
        const fechaInicio = (() => {
          if (datosGantt.data.length > 0 && datosGantt.data[0].start_date) {
            const [fechaPart, horaPart] = datosGantt.data[0].start_date.split(' ')
            const [day, month, year] = fechaPart.split('-')
            const [hour, minute] = horaPart ? horaPart.split(':') : ['00', '00']

            return new Date(
              parseInt(year),
              parseInt(month) - 1,
              parseInt(day),
              parseInt(hour),
              parseInt(minute),
            )
          }
          return new Date()
        })()

        // Usar el contenedor correcto según el modo

        const containerId = sinDistracciones ? 'gantt_scheduler_fullscreen' : 'gantt_scheduler'
        const container = document.getElementById(containerId)

        if (!container) {
          return
        }

        scheduler.init(container, fechaInicio, 'timeline_week')

        // Guardar estado original antes de cualquier cambio
        const estadosOriginales = new Map()

        scheduler.attachEvent('onBeforeDrag', function (id, mode, e) {
          const evento = scheduler.getEvent(id)

          // Solo permitir drag en actividades
          if (evento.tipo !== 'Actividad') {
            return false
          }

          // Guardar estado original
          estadosOriginales.set(id, {
            start_date: new Date(evento.start_date),
            end_date: new Date(evento.end_date),
            section_id: evento.section_id,
          })

          return true // Permitir drag
        })

        // Validar después del cambio
        scheduler.attachEvent('onEventChanged', async function (id, ev) {
          // Solo validar actividades
          if (ev.tipo !== 'Actividad') {
            return true
          }

          const original = estadosOriginales.get(id)

          // Si no cambió nada, no validar
          if (!original) {
            return true
          }

          // Convertir fechas a formatos ISO para backend
          const formatoISO = (fecha) => {
            const d = new Date(fecha)
            return d.toISOString()
          }

          // Validar con backend
          const validacion = await validarMovimiento(
            id,
            formatoISO(ev.start_date),
            formatoISO(ev.end_date),
            ev.section_id,
          )

          if (validacion.valido) {
            // Movimiento válido - marcar como pendiente
            scheduler.getEvent(id).pendiente = true
            scheduler.updateView()

            // Agregar a cambios pendientes
            const nuevoCambio = {
              id_resultado: parseInt(id),
              id_maquina: parseInt(ev.section_id),
              start_manual: formatoISO(ev.start_date),
              fin_manual: formatoISO(ev.end_date),
            }

            setCambiosPendientes((prev) => {
              const filtered = prev.filter((c) => c.id_resultado !== parseInt(id))
              const nuevaLista = [...filtered, nuevoCambio]
              return nuevaLista
            })

            enqueueSnackbar('Movimiento válido. Recuerda guardar los cambios.', {
              variant: 'success',
            })
          } else {
            // Movimiento inválido - revertir
            scheduler.getEvent(id).start_date = original.start_date
            scheduler.getEvent(id).end_date = original.end_date
            scheduler.getEvent(id).section_id = original.section_id
            scheduler.updateView()

            enqueueSnackbar(validacion.mensaje, { variant: 'error' })
          }

          // Limpiar estado original
          estadosOriginales.delete(id)

          return true
        })

        // Cargar datos desde el backend
        const eventos = datosGantt.data.map((task) => ({
          id: task.id,
          text: task.text,
          start_date: task.start_date,
          end_date: task.end_date,
          section_id: task.maquina,
          tipo: task.tipo || 'Actividad',
          tipo_transicion: task.tipo_transicion || null,
          maquina: task.maquina,
          articulo: task.articulo,
          cliente: task.cliente_nombre ? `${task.cliente} - ${task.cliente_nombre}` : task.cliente,
          cliente_nombre: task.cliente_nombre,
          pedido: task.pedido || null,
          cantidad: task.cantidad,
          retraso: task.retraso || false,
          familia: task.familia,
          esmalte: task.esmalte,
          esmalte_equivalente: task.esmalte_equivalente || null,
          codigo_horno: task.codigo_horno || null,
          duracion_horas: task.duration ?? null,
          manual: task.manual || false,
        }))

        // FIN: Lógica de mapeo

        scheduler.parse({ data: eventos, links: datosGantt.links || [] }, 'json')

        // CRÍTICO: Forzar re-render del scheduler

        setTimeout(() => scheduler.updateView(), 100)

        schedulerRef.current = scheduler
        setLoading(false)
      } catch (error) {
        setLoading(false)
      }
    }

    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = '/gantt-trial/dhtmlxscheduler.css'
    link.onload = () => {
      const s1 = document.createElement('script')
      s1.src = '/gantt-trial/dhtmlxscheduler.js'
      s1.onload = () => {
        const s2 = document.createElement('script')
        s2.src = '/gantt-trial/dhtmlxscheduler_timeline.js'
        s2.onload = () => setTimeout(inicializarScheduler, 300)
        document.body.appendChild(s2)
      }
      document.body.appendChild(s1)
    }
    document.head.appendChild(link)

    return () => {
      if (schedulerRef.current) {
        try {
          // Limpiar todos los datos
          schedulerRef.current.clearAll()

          if (typeof schedulerRef.current.destructor === 'function') {
            schedulerRef.current.destructor()
          }

          schedulerRef.current = null
        } catch (error) {
          // Error al limpiar scheduler
        }
      }
    }
  }, [datosGantt, validarMovimiento, sinDistracciones, mostrarTodasMaquinas])

  return (
    <>
      <Box sx={{ p: 2 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'end' }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h6">Planificación de Producción</Typography>
            </Box>
            <Alert severity="info">
              Arrastra para modificar fechas y máquinas • Doble click para ver detalles
            </Alert>
            <Button variant="outlined" onClick={toggleSinDistracciones} sx={{ mt: 1 }}>
              Sin Distracciones
            </Button>

            <Button
              variant="contained"
              color="primary"
              disabled={cambiosPendientes.length === 0}
              onClick={guardarCambiosPendientes}
              sx={{ mt: 1, ml: 1 }}
            >
              Guardar Cambios ({cambiosPendientes.length})
            </Button>
            <DialogoReoptimizar
              onConfirmar={confirmarReoptimizacion}
              cambiosPendientes={cambiosPendientes.length}
              sx={{ mt: 1, ml: 1 }}
            />

            <Button
              variant="contained"
              color="secondary"
              onClick={migrarAOracle}
              disabled={migrandoOracle}
              sx={{ mt: 1, ml: 1 }}
            >
              {migrandoOracle ? 'Migrando…' : 'Migrar a Oracle'}
            </Button>

            <FormControlLabel
              control={
                <Switch
                  checked={mostrarTodasMaquinas}
                  onChange={(e) => setMostrarTodasMaquinas(e.target.checked)}
                />
              }
              label="Mostrar todas las máquinas"
              sx={{ mt: 1, ml: 2 }}
            />
            <FiltrosGantt onApplyFilters={handleApplyFilters} />
          </Box>
        </Box>

        <Box sx={{ position: 'relative' }}>
          {loading && (
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10 }}>
              <Skeleton variant="rectangular" height={600} />
            </Box>
          )}

          <Box
            ref={!sinDistracciones ? containerRef : null}
            id="gantt_scheduler"
            sx={{
              width: '100%',
              height: 600,
              border: '1px solid #ddd',
              borderRadius: 1,
              bgcolor: 'white',
              position: 'relative',
            }}
          />
        </Box>
      </Box>

      {/* Dialog para modo sin distracciones */}
      <Dialog
        open={sinDistracciones}
        onClose={toggleSinDistracciones}
        maxWidth={false}
        fullScreen
        slotProps={{
          paper: {
            sx: {
              bgcolor: 'white',
            },
          },
        }}
      >
        <Box sx={{ p: 2, height: '100vh', display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Typography variant="h6">Planificación de Producción</Typography>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button variant="contained" onClick={toggleSinDistracciones}>
                Salir del modo sin distracciones
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={cambiosPendientes.length === 0}
                onClick={guardarCambiosPendientes}
              >
                Guardar Cambios ({cambiosPendientes.length})
              </Button>
              <DialogoReoptimizar
                onConfirmar={confirmarReoptimizacion}
                cambiosPendientes={cambiosPendientes.length}
              />
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={mostrarTodasMaquinas}
                    onChange={(e) => setMostrarTodasMaquinas(e.target.checked)}
                  />
                }
                label="Mostrar todas las máquinas"
                sx={{ mt: 1, ml: 2 }}
              />
            </Box>
          </Box>

          <Box sx={{ flex: 1, position: 'relative' }}>
            {loading && (
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10 }}>
                <Skeleton variant="rectangular" height="100%" />
              </Box>
            )}

            <Box
              ref={sinDistracciones ? containerRef : null}
              id="gantt_scheduler_fullscreen"
              sx={{
                width: '100%',
                height: '100%',
                border: '1px solid #ddd',
                borderRadius: 1,
                bgcolor: 'white',
                position: 'relative',
              }}
            />
          </Box>
        </Box>
      </Dialog>

      {/* Dialog para mostrar error de reoptimización */}
      <DialogoErrorReoptimizacion
        error={errorReoptimizacion}
        onClose={() => setErrorReoptimizacion(null)}
      />
    </>
  )
}
